/** Експорт поточного списку в CSV через DAO із заголовками авторизації. */
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, dao } from '@platform/dao';
import { useAppDispatch } from '@platform/state/hooks';
import { toastShown } from '@platform/state/slices/uiSlice';
import type { IRecordListModel, ISavedViewDefinition } from '@platform/core/types';
import type { IMenuEntry } from '../menu/IMenuEntry';
import { useAdvancedDsl } from './advanced/useAdvancedDsl';
import { exportParamsOf, listQueryOf } from './listQuery';
import { saveFile } from './saveFile';

/**
 * Експорт CSV: той самий запит, що й сторінка списку (фільтр і пресет пункту меню, швидкий фільтр, розширені фільтри, етап, теги, пошук, сортування, вид —
 * listQueryOf без сторінки), завантажується через fetch DAO з тими самими заголовками (X-Dev-User у Development, Bearer у продуктиві),
 * а не простим посиланням; файл зберігається з іменем із відповіді. Помилка — спливне повідомлення. `busy` — поки триває завантаження.
 * `run(extra)` додає умову фільтр-DSL (напр. `code:in:…` — лише вибрані записи); `saved` — визначення вибраного збереженого подання.
 */
export function useCsvExport(model: IRecordListModel, entry: IMenuEntry, saved?: ISavedViewDefinition) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [busy, setBusy] = useState(false);
  const { ws, rt, state } = model;
  const advanced = useAdvancedDsl(ws, rt.key);
  const run = useCallback(async (extra?: string) => {
    setBusy(true);
    try {
      const params = exportParamsOf(listQueryOf(entry, state, rt, saved, advanced));
      const file = await dao.records.exportCsv(ws, rt.key, extra ? { ...params, filter: [params.filter, extra].filter(Boolean).join(';') } : params);
      saveFile(file.blob, file.fileName);
    } catch (e) {
      dispatch(toastShown({ kind: 'error', text: t('list.exportFailed', { message: ApiError.fromUnknown(e).message }) }));
    } finally {
      setBusy(false);
    }
  }, [ws, rt, state, entry, saved, advanced, dispatch, t]);
  return { busy, run };
}
